import { Observable, of, Subject } from 'rxjs';

import { Injectable, NgZone } from '@angular/core';
import { PlatformService } from '@core/services/platform.service';

@Injectable({
    providedIn: 'root'
})
export class IntersectionService {
    private observers: {
        [key: string]: {
            entries: Set<Element>;
            observer: IntersectionObserver;
            subject: Subject<IntersectionObserverEntry>;
        };
    } = {};

    constructor(
        private platformService: PlatformService,
        private ngZone: NgZone
    ) {}

    public observe(
        key: string,
        element: Element,
        options: IntersectionObserverInit = {}
    ): Observable<IntersectionObserverEntry> {
        if (this.platformService.isServer) {
            return of({ target: element, isIntersecting: false } as IntersectionObserverEntry);
        }

        if (!this.observers[key]) {
            this.observers[key] = {
                entries: new Set(),
                observer: this.ngZone.runOutsideAngular(
                    () =>
                        new IntersectionObserver(observerEntries => {
                            observerEntries.forEach(entry => this.observers[key]?.subject.next(entry));
                        }, options)
                ),
                subject: new Subject<IntersectionObserverEntry>()
            };
        }

        const { entries, observer } = this.observers[key];

        entries.add(element);
        observer.observe(element);

        return new Observable(observerSubject => {
            this.observers[key].subject.subscribe(entry => observerSubject.next(entry));
            return () => {
                this.unobserve(key, element);
                observerSubject.complete();
            };
        });
    }

    private unobserve(key: string, element: Element): void {
        if (!this.observers[key]) {
            return;
        }

        const { entries, observer, subject } = this.observers[key];

        if (entries.has(element)) {
            observer.unobserve(element);
            entries.delete(element);
        }

        if (entries.size === 0) {
            subject.complete();
            observer.disconnect();
            delete this.observers[key];
        }
    }
}
